'use client';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { faPhone } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { generateWhatsappLink } from '@/utils/formatters/format-whatsapp';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { getProfessionalArticle } from '@/utils/formatters/professional-name';
import { Partner } from '@/types';

interface StandaloneSupportProps {
  entityName: string;
  partner: Partner;
  guideCode: string;
  documentNumber: string;
}

export function StandaloneSupport({ entityName, partner, guideCode, documentNumber }: StandaloneSupportProps) {
  const whatsappLink = useMemo(() => {
    const text = `Olá, comprei um horário no site da Medprev e gostaria de fazer o agendamento do horário.
      ${' '}
      GUIA: ${guideCode}. CPF: ${documentNumber}`;
    return generateWhatsappLink(partner.facility.contacts.whatsapp, text);
  }, [documentNumber, guideCode, partner.facility.contacts.whatsapp]);

  const articleBeforeName = getProfessionalArticle(entityName);

  return (
    <div
      data-testid={DataTestIdEnum.APPOINTMENT_CARD_STANDALONE_SUPPORT}
      className="flex flex-col gap-3 border border-warning-400 rounded-xl p-4"
    >
      <div className="flex items-center gap-2">
        <Icon icon={faCircleExclamation} className="text-warning-400 text-xl flex-shrink-0" />
        <Typography variant={TypographyVariant.BODY_TEXT} className="font-bold text-gray-900">Guia Avulsa</Typography>
      </div>

      <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
        {`A data e o horário de atendimento devem ser combinados diretamente com ${articleBeforeName} `}
        <strong>{entityName}</strong>
        , pessoalmente ou através dos contatos abaixo.
      </Typography>

      {partner.facility.contacts.whatsapp && (
        <Button asChild startIcon={faWhatsapp} variant="secondary">
          <Link prefetch={false} href={whatsappLink} target="_blank" rel="noopener">
            {maskPhone(partner.facility.contacts.whatsapp)}
          </Link>
        </Button>
      )}

      {partner.facility.contacts.phones[0] && (
        <Button asChild startIcon={faPhone} variant="secondary">
          <Link prefetch={false} href={`tel:${partner.facility.contacts.phones[0]}`} target="_blank" rel="noopener">
            {maskPhone(partner.facility.contacts.phones[0])}
          </Link>
        </Button>
      )}
    </div>
  );
}
